/* Updated WelcomeCard.tsx with dynamic nickname */
import React from "react";

export type WelcomeCardProps = {
  examples?: string[];
  footerNote?: string;
  compact?: boolean;
  selfInitiatedPermitted?: boolean;
  onPick?: (text: string) => void;
  nickname?: string; // <-- add nickname prop
};

const BASE_EXAMPLES: string[] = [
  "What's my homework?",
  "Practice coping skills",
  "Open learning buddy",
];

export default function WelcomeCard({
  examples,
  footerNote,
  compact,
  onPick,
  selfInitiatedPermitted,
  nickname,
}: WelcomeCardProps) {

  const defaultItems = React.useMemo(() => {
    const lastExample = selfInitiatedPermitted
      ? "I want to report an exposure"
      : "Remind me of my coping strategies";
    return [...BASE_EXAMPLES, lastExample];
  }, [selfInitiatedPermitted]);

  const items = examples && examples.length > 0 ? examples : defaultItems;

  const cleanName = nickname?.trim();

  const greeting = cleanName
    ? `Hi, ${cleanName}!`
    : "Hi!";

  const mobileGreetingText = "What would you like to work on today?";

  const desktopGreetingText =
    "What would you like to work on today? I can help you work through your ERP homework, practice coping skills, and track your SUDS.";

  return (
    <div className="w-full">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-2xl border border-gray-200 bg-white/90 shadow-sm p-5 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-lg font-semibold tracking-tight">
                <span>{greeting}</span>{" "}
                <span className="sm:hidden">{mobileGreetingText}</span>
                <span className="hidden sm:inline">{desktopGreetingText}</span>
              </h1>
              <p className="mt-1 text-sm sm:text-xs text-gray-600">
                You can type in the box, use dictation, or tap one of the examples to get started.
              </p>
            </div>
          </div>

          <div className={compact ? "mt-4" : "mt-6"}>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
              Examples you can try
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {items.map((text, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onPick?.(text)}
                  className="w-full text-left rounded-xl border border-gray-200 px-3 py-2 sm:px-3.5 sm:py-2.5 bg-white hover:bg-gray-50 active:bg-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  aria-label={`Send message: ${text}`}
                >
                  <div className="text-[13px] sm:text-sm font-medium text-gray-900 whitespace-normal break-words leading-snug">
                    {text}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="text-xs text-gray-500">
              {footerNote ?? (
                <>
                  ⚠️ This isn't for emergencies. If you're in the U.S. and need help right away, call {" "}
                  <span className="font-medium">988</span>.
                </>
              )}
            </div>
            <div className="sm:ml-auto text-xs text-gray-400">
              💡 Tip: Tap an example to send it right away.            </div>
          </div>
        </div>

        {!compact && (
          <div className="mt-3 text-[13px] text-gray-500">
            <span className="font-medium text-gray-700">🔒 Privacy:</span>{" "}
            Nothing is saved on your device except your access code.
          </div>
        )}
      </div>
    </div>
  );
}
